import React, { FunctionComponent, KeyboardEvent, ReactElement, ReactNode, useCallback, useRef, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent, TextStyle, View, ViewStyle } from 'react-native'

import { Image as ExpoImage } from 'expo-image'
import { includes, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { auroraBaseColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/auroraColors'
import { getStringFromMessage } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getStringFromMessage'
import {
  RadioButtonType,
  TitleInfoPopover,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { FieldBadge } from './FieldBadge'
import { getRadioButtonStyles } from './styles/radioButtonStyles'
import { BadgeVariant } from '../../badge/constants'
import { AccessibilityRolesNative, breakpoints, IS_WEB } from '../../constants'
import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { UICoreFormContext } from '../../organisms/formBody/types'
import { BodyTextSize, StyledText, TextType } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText, Size } from '../bodyText/BodyText'
import { BaseInput } from '../formElements/BaseInput'
import { CheckIcon } from '../icons/CheckIcon'
import { SessionCompletedCheck } from '../icons/SessionCompletedCheck'
import { XIconRound } from '../icons/XIconRound'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../title/Title'
import { ToolTipTriggerConfig } from '../tooltip/Tooltip'

import type * as CSS from 'csstype'

export type RadioGroupValue = number | string | boolean | null

type NestedComponentProps = {
  baseInputStyle?: ViewStyle
  error?: string
  value?: string | number | Date | boolean
  time?: string | Date
  label?: string
  options?: Array<{ label: string; value: string | number }>
  onBlur?: () => void
  onChange?: (value: any) => void
  onChangeAMorPM?: (value: any) => void
  onFocus?: () => void
  style?: ViewStyle
  defaultAMorPM?: string
  onError?: (value: any) => void
  name?: string
}

export type RadioButtonProps = {
  title: ReactNode
  description?: ReactNode
  badge?: ReactNode
  selected: boolean
  ImageComponent?: React.ComponentType<ImageComponentProps>
  backgroundImageUri?: string
  value: RadioGroupValue
  onChange: (value: RadioGroupValue) => void
  readOnly?: boolean
  highlight?: boolean
  error?: string
  onFocus?: () => void
  focusable?: boolean
  onBlur?: () => void
  isYesNo?: boolean
  buttonType?: RadioButtonType
  parentContainerWidth: number
  gridButtonsGap: number
  widthWrapToContent?: boolean
  buttonStyle?: ViewStyle
  size?: RadioButtonSize
  NestedTimeField?: React.ComponentType<any>
  nestedTimeFieldProps?: NestedComponentProps
  NestedSelectField?: React.ComponentType<any>
  nestedSelectFieldProps?: NestedComponentProps
  selectedCheckmark?: boolean
  DecorativeIcon?: React.ComponentType<any>
  textColor?: string
  // TODO: Remove once PaymentForm migrates away from CIRCLE_TEXT_OUTLINE with a transparent buttonStyle override.
  // Exists solely to keep indicator visible on the HPI payment page's transparent dark cards.
  indicatorColor?: string
  disabled?: boolean

  errorMessageId?: string
}

export type RadioGroupProps = {
  label?: string
  labelNativeID?: string
  labelInfoPopover?: TitleInfoPopover
  subLabel?: string
  subLabelComponent?: string
  labelSecondary?: string
  largeSubLabel?: boolean
  labelAlignment?: CSS.Properties['alignItems']
  selectedValue?: RadioGroupValue
  name?: string
  error?: string
  buttons: Array<{
    label: string | React.ReactNode
    value: RadioGroupValue
    highlight?: boolean
    description?: ReactNode
    badge?: ReactNode
    inactive?: boolean
    imageComponent?: React.ComponentType<ImageComponentProps>
    backgroundImageUri?: string
    schema?: any
    nestedTimeField?: React.ComponentType<any>
    nestedTimeFieldProps?: NestedComponentProps
    nestedSelectField?: React.ComponentType<any>
    nestedSelectFieldProps?: NestedComponentProps
    decorativeIcon?: React.ComponentType<any>
    selected?: boolean
    textColor?: string
  }>
  onChange: (value: RadioGroupValue) => void
  readOnly?: boolean
  showDescription?: boolean
  correctValueDetails?: string
  correctValue?: RadioGroupValue
  onFocus?: () => void
  onBlur?: () => void
  isYesNo?: boolean
  buttonType?: RadioButtonType
  toolTipContent?: string | ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  active?: boolean
  accessibilityLabelledBy?: string
  widthWrapToContent?: boolean
  parentContainerWidth?: number
  baseInputStyle?: ViewStyle
  buttonStyle?: ViewStyle
  contextProps?: UICoreFormContext
  size?: RadioButtonSize
  selectedCheckmark?: boolean
  disabled?: boolean
  minLabel?: string
  minLabelTestId?: string
  maxLabel?: string
  maxLabelTestId?: string
  customTextStyle?: TextStyle
  labelContainerStyle?: ViewStyle
  isRadioButtonsFlexRowDirection?: boolean
  buttonsGap?: number
  // TODO: Remove once PaymentForm migrates away from CIRCLE_TEXT_OUTLINE with a transparent buttonStyle override.
  // Exists solely to keep indicator and text visible on the HPI payment page's transparent dark cards.
  textColor?: string
  indicatorColor?: string
}

type GetButtonStylesArgs = {
  buttonType: RadioButtonType
  hovered?: boolean
  pressed?: boolean
  readOnly?: boolean
  selected?: boolean
  highlight?: boolean
  description?: string
  selectedDescriptionReadOnly?: boolean
}

export type ImageComponentProps = {
  width?: number
  height?: number
  selected?: boolean
}

export enum RadioButtonSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

const CorrectDescriptionContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundSection,
  padding: `${spacing['12px']} ${spacing['16px']}`,
  flexDirection: 'column',
  borderRadius: '8px',
  marginTop: spacing['4px'],
}))

const TopCorrectDescriptionContainer = styled.View({
  flexDirection: 'row',
})

const BottomCorrectDescriptionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingLeft: theme.spacing['40px'],
}))

const CorrectDescriptionLabel = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  paddingBottom: theme.spacing['4px'],
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingRight: theme.spacing['12px'],
}))

const RadioButtonsContainer = styled.View<{
  buttonType: RadioButtonType
  flexRowDirection?: boolean
  gridButtonsGap: number
  buttonsGap?: number
}>(({ buttonType, flexRowDirection, gridButtonsGap, buttonsGap }) => ({
  ...(buttonType === RadioButtonType.GRID && {
    margin: `-${gridButtonsGap / 2}px`,
    alignSelf: 'center',
  }),
  ...(buttonType === RadioButtonType.CIRCULAR_WITH_NUMBER && {
    justifyContent: 'space-between',
  }),
  ...(flexRowDirection && {
    flexDirection: 'row',
    flexWrap: 'wrap',
  }),
  ...(buttonsGap && {
    gap: `${buttonsGap}px`,
  }),
}))

const RadioButtonContainer = styled(PressableOpacity)<{
  buttonType: RadioButtonType
  gridButtonsGap: number
  parentContainerWidth: number
  isYesNo?: boolean
  widthWrapToContent?: boolean
}>`
  flex-direction: row;
  ${({ buttonType, parentContainerWidth, isYesNo, gridButtonsGap, widthWrapToContent }) => {
    if (isYesNo) {
      if (buttonType === RadioButtonType.TEXT_OUTLINE) {
        return `
          width: 64px;
          justify-content: center;
          margin-right: 8px;
        `
      } else if (buttonType === RadioButtonType.CIRCLE_TEXT_OUTLINE) {
        return `
          min-width: 107.5px;
          justify-content: center;
          margin-right: 16px;
        `
      }
    } else if (widthWrapToContent) {
      return `
        align-self: flex-start;
      `
    } else {
      if (buttonType === RadioButtonType.GRID) {
        const width = getRadioButtonWidth(parentContainerWidth, gridButtonsGap)
        return `
          width: ${width}px;
          margin: ${gridButtonsGap / 2}px;
        `
      } else if (buttonType === RadioButtonType.CIRCULAR_WITH_NUMBER) {
        return `
          align-items: center;
        `
      }
    }
    return ``
  }}
`

const RadioButtonFocused = styled(View)<{ buttonType: RadioButtonType; focused: boolean; image: boolean }>(
  ({ image, focused, buttonType, theme }) => ({
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    border: focused ? `1px solid ${theme.colors.radioOutlineFocused}` : 'none',
    ...(!image && { marginRight: '16px' }),
    ...(buttonType === RadioButtonType.GRID && { position: 'absolute', left: '16px', top: '16px' }),
    ...((buttonType === RadioButtonType.CIRCLE_TEXT_OUTLINE ||
      buttonType === RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE ||
      buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE) && {
      marginRight: '12px',
    }),
  }),
)

const RadioButtonCircle = styled(View)<{
  theme: ThemeType
  selected: boolean
  focused: boolean
  hovered: boolean
  readOnly?: boolean
  error?: string
  buttonType?: RadioButtonType
  indicatorColor?: string
}>`
  ${({ theme }) => theme && `background-color: ${theme.colors.radioBackgroundPrimary}`};
  width: 20px;
  height: 20px;
  border-radius: 10px;
  margin: auto;
  ${({ theme, selected, readOnly, hovered, error, buttonType, indicatorColor }) => {
    const isAuroraDarkTheme = theme.isAuroraTheme && theme.isDarkMode
    if (error && !readOnly) {
      return `border: 1px solid ${theme.colors.borderError};`
    }
    if (buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE && selected && isAuroraDarkTheme) {
      return `border: 1px solid ${readOnly ? theme.colors.radioOutlineInactive : auroraBaseColors.white};`
    }
    if (hovered && !(selected && isAuroraDarkTheme) && buttonType !== RadioButtonType.GRID) {
      return `border: 1px solid ${theme.colors.radioOutlineHover};`
    }
    return `border: 1px solid ${
      selected
        ? readOnly
          ? theme.colors.radioOutlineSelectedInactive
          : indicatorColor || theme.colors.radioOutlineSelected
        : readOnly
        ? theme.colors.radioOutlineInactive
        : theme.colors.radioOutlineDefault
    };`
  }}
`

const RadioButtonFilledIn = styled(View)<{
  theme: ThemeType
  readOnly?: boolean
  buttonType?: RadioButtonType
  indicatorColor?: string
}>`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  margin: auto;
  ${({ theme, readOnly, buttonType, indicatorColor }) => {
    const isAuroraDarkTheme = theme.isAuroraTheme && theme.isDarkMode
    if (buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE && isAuroraDarkTheme) {
      return `background-color: ${readOnly ? theme.colors.radioOutlineInactive : auroraBaseColors.white};`
    }
    return `background-color: ${
      readOnly ? theme.colors.radioBackgroundSelectedInactive : indicatorColor || theme.colors.radioBackgroundSelected
    };`
  }}
`

const RadioButtonContent = styled(View)<{ buttonType: RadioButtonType }>`
  flex: 1;
  ${({ buttonType }) => {
    switch (buttonType) {
      case RadioButtonType.CIRCLE_TEXT_OUTLINE:
        return `
          flex-direction: row;
          flex-basis: auto;
          align-items: center;
        `
      case RadioButtonType.GRID:
        return `
          flex-shrink: 0;
          flex-direction: column;
          align-items: center;
          margin: auto;
        `
      default:
        return ``
    }
  }}
`

const RadioButtonImageContainer = styled(View)<{ buttonType: RadioButtonType; size?: RadioButtonSize }>(
  ({ theme: { spacing }, buttonType, size }) => {
    const imageSize = size === RadioButtonSize.SMALL ? '40px' : '48px'
    return {
      position: 'relative',
      height: imageSize,
      width: imageSize,
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: spacing['8px'],
      marginRight: spacing['8px'],
      ...(buttonType === RadioButtonType.GRID && { marginBottom: '8px' }),
    }
  },
)

const RadioButtonImageTransition = styled(View)<{ scaled?: boolean }>(({ scaled }) => ({
  ...(IS_WEB && { transition: 'transform 0.3s' }),
  position: 'absolute',
  height: '100%',
  width: '100%',
  transform: scaled ? `scale(${53 / 48})` : 'scale(1)',
}))

const RadioButtonText = styled(StyledText)<{
  theme: ThemeType
  buttonType: RadioButtonType
  selected?: boolean
  highlight?: boolean
  readOnly?: boolean
  error?: string
  badge?: boolean
  textColor?: string
}>`
  ${({ badge }) => badge && `padding-right: 8px;`}
  ${IS_WEB ? 'flex-grow: 1' : 'flex-shrink: 1'};
  ${({ theme, buttonType, selected, highlight, readOnly, error, textColor }) => {
    switch (buttonType) {
      case RadioButtonType.CIRCLE_TEXT_OUTLINE:
        if (highlight) {
          return `color: ${theme.colors.radioWithOutlinePrimaryTextHighlight};`
        }
        return `
          color: ${
            selected
              ? textColor || theme.colors.radioWithOutlinePrimaryTextSelected
              : readOnly
              ? theme.colors.radioWithOutlinePrimaryTextInactive
              : theme.colors.radioWithOutlineSecondaryTextDefault
          };
        `
      case RadioButtonType.DESCRIPTION_OUTLINE:
      case RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE:
        if (highlight) {
          return `color: ${theme.colors.radioWithOutlinePrimaryTextHighlight};`
        }
        return `
            color: ${
              selected
                ? theme.colors.radioWithOutlinePrimaryTextSelected
                : readOnly
                ? theme.colors.radioWithOutlinePrimaryTextInactive
                : theme.colors.radioWithOutlinePrimaryTextDefault
            };
          `
      case RadioButtonType.DESCRIPTION_NO_OUTLINE:
        if (error && !readOnly) {
          return `color: ${theme.colors.textError};`
        }
        return `
          color: ${
            readOnly
              ? theme.colors.radioWithOutlinePrimaryTextInactive
              : theme.colors.radioWithOutlinePrimaryTextDefault
          };
        `
      case RadioButtonType.GRID:
        return `
          flex-shrink: 0;
          align-self: center;
          text-align: center;
          color: ${selected ? theme.colors.radioTextSelected : theme.colors.radioTextDefault};
          `
      case RadioButtonType.CIRCULAR_WITH_NUMBER:
        return `
              align-self: center;
              ${selected ? `color: ${theme.colors.radioTextSelected};` : ''}
            `
      default:
        if (highlight) {
          return `color: ${selected ? theme.colors.radioTextSelected : theme.colors.radioTextHighlight};`
        } else if (textColor) {
          return `color: ${textColor}`
        } else if (selected) {
          return `color: ${theme.colors.radioTextSelected};`
        }
        return `color: ${theme.colors.radioTextDefault};`
    }
  }};
`

const RadioButtonDescription = styled(StyledText)<{
  theme: ThemeType
  readOnly?: boolean
  buttonType: RadioButtonType
  selected: boolean
}>`
  ${({ theme, readOnly, buttonType, selected }) => {
    if (
      buttonType === RadioButtonType.DESCRIPTION_OUTLINE ||
      buttonType === RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE
    ) {
      if (selected) {
        return `color: ${theme.colors.radioWithOutlineSecondaryTextSelected};`
      }
      if (readOnly) {
        return `color: ${theme.colors.radioWithOutlineSecondaryTextInactive};`
      }
    }
    return `color: ${
      readOnly ? theme.colors.radioWithOutlineSecondaryTextInactive : theme.colors.radioWithOutlineSecondaryTextDefault
    };`
  }}
`

const HeadingContainer = styled(View)<{ flexDirectionRow?: boolean }>(({ flexDirectionRow }) => ({
  flexGrow: 1,
  flexShrink: 1,
  flexDirection: flexDirectionRow ? 'row' : 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
}))

const DecorativeIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const NestedComponentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const SessionCompletedCheckContainer = styled.View({
  display: 'flex',
  marginLeft: 'auto',
  marginRight: 0,
  alignSelf: 'flex-end',
  justifyContent: 'flex-end',
})

const MinMaxLabelsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: theme.spacing['4px'],
}))

export const getRadioButtonWidth = (parentContainerWidth: number, gridButtonsGap: number) => {
  const itemsPerRow = parentContainerWidth > breakpoints.mobileS ? 3 : parentContainerWidth > 300 ? 2 : 1
  const totalGapSize = itemsPerRow * gridButtonsGap
  const buttonWidth = (parentContainerWidth - totalGapSize) / itemsPerRow
  return buttonWidth
}

export const RadioButton: FunctionComponent<RadioButtonProps> = ({
  title,
  description,
  badge,
  ImageComponent,
  backgroundImageUri,
  selected,
  value,
  onChange,
  readOnly,
  highlight,
  error,
  onFocus = noop,
  focusable,
  onBlur = noop,
  isYesNo = false,
  buttonType = RadioButtonType.TEXT_OUTLINE,
  parentContainerWidth,
  gridButtonsGap,
  widthWrapToContent,
  buttonStyle = {},
  size = RadioButtonSize.DEFAULT,
  NestedTimeField,
  nestedTimeFieldProps,
  NestedSelectField,
  nestedSelectFieldProps,
  selectedCheckmark,
  DecorativeIcon,
  textColor,
  indicatorColor,
  disabled,
  errorMessageId,
}) => {
  const {
    colors,
    breakpoints: { isMobileSized },
    isAuroraTheme,
  } = useTheme() as ThemeType
  parentContainerWidth = Math.floor(parentContainerWidth)
  const [focusVisible, setFocusVisible] = useState(false)
  const focusVisibleCallback = useCallback(
    (focus) => {
      if (buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE) setFocusVisible(focus)
    },
    [buttonType],
  )
  const [hoverVisible, setHoverVisible] = useState(false)

  const getButtonStyles = ({
    buttonType,
    hovered,
    pressed,
    readOnly,
    selected,
    highlight,
    selectedDescriptionReadOnly,
  }: GetButtonStylesArgs) => {
    let state = 'active'
    if (hovered) state = 'hovered'
    if (pressed) state = 'pressed'
    if (readOnly) state = 'readOnly'
    if (highlight) state = 'highlight'
    if (selected) state = 'selected'
    if (selectedDescriptionReadOnly) state = 'selectedDescriptionReadOnly' // temp state till the new redesign for q5
    const radioButtonStyles = getRadioButtonStyles(colors, isMobileSized)
    const styles = {
      ...radioButtonStyles[buttonType].base,
      ...radioButtonStyles[buttonType].sizes[size],
      ...radioButtonStyles[buttonType].states[state],
      ...buttonStyle,
    }
    return styles
  }

  const isAuroraGridTile = isAuroraTheme && buttonType === RadioButtonType.GRID && !!backgroundImageUri
  const isAuroraGridTileSelected = isAuroraGridTile && selected
  const showCircle =
    (buttonType === RadioButtonType.CIRCLE_TEXT_OUTLINE ||
      buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE ||
      buttonType === RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE ||
      buttonType === RadioButtonType.GRID) &&
    !isAuroraGridTile
  const selectedDescriptionReadOnly =
    (buttonType === RadioButtonType.DESCRIPTION_OUTLINE || buttonType === RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE) &&
    selected &&
    readOnly
  return (
    <RadioButtonContainer
      onPress={() => onChange(value)}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={readOnly || disabled}
      hitSlop={{ top: 5, right: 24, bottom: 5, left: 24 }}
      accessibilityLabel={getStringFromMessage(title)}
      accessibilityRole='radio'
      accessibilityChecked={selected}
      accessibilityState={{ checked: selected }}
      accessibilityDisabled={readOnly}
      importantForAccessibility='yes'
      aria-describedby={error && errorMessageId ? errorMessageId : undefined}
      focusable={focusable || selected}
      showFocusIndicator={buttonType !== RadioButtonType.DESCRIPTION_NO_OUTLINE}
      getFocusVisible={focusVisibleCallback}
      getHoverVisible={(hover) => setHoverVisible(hover)}
      testID={tID(`RadioButton-${value}`)}
      style={{
        ...getButtonStyles({
          buttonType,
          selected,
          hovered: hoverVisible,
          readOnly,
          highlight,
          selectedDescriptionReadOnly,
        }),
        ...(isAuroraGridTile && !isAuroraGridTileSelected && { overflow: 'hidden', borderWidth: 0 }),
      }}
      buttonType={buttonType}
      isYesNo={isYesNo}
      parentContainerWidth={parentContainerWidth}
      gridButtonsGap={gridButtonsGap}
      widthWrapToContent={widthWrapToContent}
      pressedOpacity={isAuroraTheme ? 1 : 0.6}
      styleByState={(pressed) =>
        pressed
          ? getButtonStyles({
              buttonType,
              pressed,
              selected,
              readOnly,
              highlight,
              selectedDescriptionReadOnly,
            })
          : {}
      }
    >
      {({ hovered }) => (
        <>
          {isAuroraGridTile && !isAuroraGridTileSelected && (
            <ExpoImage
              source={{ uri: backgroundImageUri }}
              contentFit='cover'
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              accessibilityIgnoresInvertColors
            />
          )}
          {showCircle && (
            <RadioButtonFocused
              buttonType={buttonType}
              focused={focusVisible}
              image={!!ImageComponent}
              importantForAccessibility='no-hide-descendants'
            >
              <RadioButtonCircle
                selected={selected}
                focused={focusVisible}
                hovered={hoverVisible}
                readOnly={readOnly}
                error={error}
                buttonType={buttonType}
                indicatorColor={indicatorColor}
              >
                {selected && (
                  <RadioButtonFilledIn readOnly={readOnly} buttonType={buttonType} indicatorColor={indicatorColor} />
                )}
              </RadioButtonCircle>
            </RadioButtonFocused>
          )}
          <RadioButtonContent buttonType={buttonType}>
            {ImageComponent && !isAuroraGridTile && !backgroundImageUri && (
              <RadioButtonImageContainer
                buttonType={buttonType}
                size={size}
                importantForAccessibility='no-hide-descendants'
              >
                <RadioButtonImageTransition scaled={hovered || selected}>
                  <ImageComponent selected={selected} />
                </RadioButtonImageTransition>
              </RadioButtonImageContainer>
            )}
            <HeadingContainer
              flexDirectionRow={selectedCheckmark || (!IS_WEB && buttonType === RadioButtonType.CIRCULAR_WITH_NUMBER)}
              testID={tID('RadioButton-headingContainer')}
              importantForAccessibility='no-hide-descendants'
            >
              {DecorativeIcon && (
                <DecorativeIconContainer testID={tID('RadioButton-decorativeIconContainer')}>
                  <DecorativeIcon />
                </DecorativeIconContainer>
              )}
              <RadioButtonText
                type={TextType.BODY}
                size={BodyTextSize.DEFAULT}
                buttonType={buttonType}
                selected={selected}
                highlight={highlight}
                readOnly={readOnly}
                error={error}
                badge={badge !== undefined}
                textColor={textColor}
              >
                {title}
              </RadioButtonText>
              {selected && selectedCheckmark && (
                <SessionCompletedCheckContainer>
                  <SessionCompletedCheck fillColor='white' backgroundColor={colors.backgroundHover} />
                </SessionCompletedCheckContainer>
              )}
            </HeadingContainer>
            {badge !== undefined && <FieldBadge text={badge} selected={selected} variant={BadgeVariant.INFO_PRIMARY} />}
            {(buttonType === RadioButtonType.DESCRIPTION_NO_OUTLINE ||
              buttonType === RadioButtonType.DESCRIPTION_OUTLINE ||
              buttonType === RadioButtonType.CIRCLE_DESCRIPTION_OUTLINE) && (
              <RadioButtonDescription
                type={TextType.BODY}
                size={BodyTextSize.DEFAULT}
                buttonType={buttonType}
                readOnly={readOnly}
                selected={selected}
              >
                {description}
              </RadioButtonDescription>
            )}
            {selected && NestedSelectField && (
              <NestedComponentContainer>
                <NestedSelectField {...nestedSelectFieldProps} />
              </NestedComponentContainer>
            )}
            {selected && NestedTimeField && (
              <NestedComponentContainer>
                <NestedTimeField {...nestedTimeFieldProps} />
              </NestedComponentContainer>
            )}
          </RadioButtonContent>
        </>
      )}
    </RadioButtonContainer>
  )
}

export const RadioGroup: FunctionComponent<RadioGroupProps> = ({
  label,
  labelNativeID,
  labelInfoPopover,
  subLabel,
  subLabelComponent,
  largeSubLabel,
  labelSecondary,
  labelAlignment,
  selectedValue,
  buttons,
  onChange,
  name,
  error,
  readOnly,
  showDescription,
  correctValueDetails,
  correctValue,
  onFocus = noop,
  onBlur = noop,
  isYesNo = false,
  buttonType = RadioButtonType.TEXT_OUTLINE,
  toolTipContent,
  toolTipTriggerConfig,
  active,
  accessibilityLabelledBy,
  widthWrapToContent,
  parentContainerWidth,
  baseInputStyle,
  buttonStyle,
  contextProps,
  size,
  selectedCheckmark,
  disabled,
  minLabel,
  minLabelTestId,
  maxLabel,
  maxLabelTestId,
  customTextStyle,
  labelContainerStyle,
  isRadioButtonsFlexRowDirection,
  buttonsGap,
  textColor: groupTextColor,
  indicatorColor,
}) => {
  const {
    colors,
    breakpoints: { isMobileSized },
  } = useTheme() as ThemeType
  const initialSelectedButtonIndex = buttons?.findIndex((button) => button.value === selectedValue)
  const initialFocusIndex = initialSelectedButtonIndex > -1 ? initialSelectedButtonIndex : 0
  const [focusIndex, setFocusIndex] = useState(initialFocusIndex)
  const { isMinWidthLaptop } = useMediaQuerySize()
  const radioContainerRef = useRef<any>(null)
  useAccessibilityFocus({ active, delay: 200, ref: radioContainerRef })
  const gridButtonsGap = isMinWidthLaptop ? 16 : 8
  const { formatMessage } = useIntl()

  // Generate errorMessageId for proper aria-describedby association
  const errorMessageId = name ? `${name}-error-message` : undefined

  // Build accessibility attributes for the radio group
  // On web, use aria-labelledby to associate with the label
  // On native, use accessibilityLabel with label as primary fallback
  const finalLabelNativeID = labelNativeID || (name ? `${name}-label` : undefined)
  const accessibilityAttr =
    !accessibilityLabelledBy && IS_WEB && finalLabelNativeID && label
      ? { 'aria-labelledby': finalLabelNativeID }
      : accessibilityLabelledBy
      ? { accessibilityLabelledBy }
      : { accessibilityLabel: label ? label : '' }

  const setFocus = (index: number) => {
    setFocusIndex(index)
    if (!buttons[index].inactive) {
      onChange(buttons[index].value)
      radioContainerRef?.current?.children?.[index]?.focus()
    }
  }

  const focusNext = () => {
    if (focusIndex < buttons.length - 1) {
      setFocus(focusIndex + 1)
    } else {
      setFocus(0)
    }
  }

  const focusPrevious = () => {
    if (focusIndex === 0) {
      setFocus(buttons.length - 1)
    } else {
      setFocus(focusIndex - 1)
    }
  }

  const handleKeyPress = (event: KeyboardEvent) => {
    const keyPressed = event.key
    const keys = [KEYS.SPACE, KEYS.ARROW_UP, KEYS.ARROW_DOWN, KEYS.ARROW_LEFT, KEYS.ARROW_RIGHT]
    if (includes(keys, keyPressed)) {
      event.stopPropagation()
      event.preventDefault()
    }
    switch (keyPressed) {
      case KEYS.ARROW_UP:
        focusPrevious()
        break
      case KEYS.ARROW_DOWN:
        focusNext()
        break
      case KEYS.ARROW_LEFT:
        focusPrevious()
        break
      case KEYS.ARROW_RIGHT:
        focusNext()
        break
      default:
        break
    }
  }

  const [radioGroupWidth, setRadioGroupWidth] = useState(0)
  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout
    setRadioGroupWidth(width)
  }

  return (
    <BaseInput
      label={label}
      labelInfoPopover={labelInfoPopover}
      subLabel={subLabel}
      subLabelComponent={subLabelComponent}
      labelAlignment={labelAlignment}
      labelSecondary={labelSecondary}
      largeSubLabel={largeSubLabel}
      name={name}
      error={error}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      hideErrorPadding
      errorMessageId={errorMessageId}
      style={{
        opacity: radioGroupWidth === 0 ? 0 : undefined,
        ...baseInputStyle,
      }}
      contextProps={contextProps}
      customTextStyle={customTextStyle}
      labelContainerStyle={labelContainerStyle}
      labelNativeID={finalLabelNativeID}
    >
      <RadioButtonsContainer
        // @ts-ignore next-line
        accessibilityRole={accessibilityLabelledBy && IS_WEB ? 'group' : AccessibilityRolesNative.RADIO_GROUP}
        onKeyDown={handleKeyPress}
        ref={radioContainerRef}
        buttonType={buttonType}
        gridButtonsGap={gridButtonsGap}
        flexRowDirection={
          (isYesNo && buttonType !== RadioButtonType.DESCRIPTION_NO_OUTLINE) ||
          buttonType === RadioButtonType.GRID ||
          buttonType === RadioButtonType.CIRCULAR_WITH_NUMBER ||
          isRadioButtonsFlexRowDirection
        }
        buttonsGap={buttonsGap}
        onLayout={onLayout}
        {...accessibilityAttr}
      >
        {buttons.map(
          (
            {
              label,
              value,
              highlight,
              description,
              badge,
              inactive,
              imageComponent,
              backgroundImageUri,
              nestedTimeField,
              nestedTimeFieldProps,
              nestedSelectField,
              nestedSelectFieldProps,
              decorativeIcon,
              textColor,
            },
            index,
          ) => {
            return (
              <RadioButton
                onChange={onChange}
                key={index}
                title={label}
                description={description}
                badge={badge}
                ImageComponent={imageComponent}
                backgroundImageUri={backgroundImageUri}
                value={value}
                selected={selectedValue === value}
                readOnly={readOnly || inactive}
                highlight={highlight}
                error={error}
                onFocus={() => {
                  setFocusIndex(index)
                  onFocus()
                }}
                // https://lyrahealth.atlassian.net/browse/LYRA-10778 For native mobile accessiblity we need this to always be focusable
                focusable={IS_WEB ? index === focusIndex : true}
                onBlur={onBlur}
                isYesNo={isYesNo}
                buttonType={buttonType}
                parentContainerWidth={
                  parentContainerWidth && radioGroupWidth === 0 ? parentContainerWidth : radioGroupWidth
                }
                gridButtonsGap={gridButtonsGap}
                widthWrapToContent={widthWrapToContent}
                buttonStyle={buttonStyle}
                size={size}
                NestedTimeField={nestedTimeField}
                nestedTimeFieldProps={nestedTimeFieldProps}
                NestedSelectField={nestedSelectField}
                nestedSelectFieldProps={nestedSelectFieldProps}
                selectedCheckmark={selectedCheckmark}
                DecorativeIcon={decorativeIcon}
                textColor={textColor || groupTextColor}
                indicatorColor={indicatorColor}
                disabled={disabled}
                errorMessageId={errorMessageId}
              />
            )
          },
        )}
      </RadioButtonsContainer>
      {showDescription && (
        <CorrectDescriptionContainer>
          <TopCorrectDescriptionContainer>
            <IconContainer testID={tID(`RadioButtons-correctIcon-${correctValue === selectedValue}`)}>
              {correctValue === selectedValue ? (
                <CheckIcon fillColor={colors.iconSuccess} size={24} />
              ) : (
                <XIconRound fillColor={colors.iconError} backgroundColor={colors.backgroundIcon} size={24} />
              )}
            </IconContainer>
            <CorrectDescriptionLabel
              size={TitleSize.XSMALL}
              noRole
              color={correctValue === selectedValue ? colors.textSuccess : colors.textError}
              text={
                correctValue === selectedValue
                  ? formatMessage({
                      defaultMessage: 'Correct Entry',
                      description: 'label that indicates the correct entry',
                    })
                  : formatMessage({
                      defaultMessage: 'Incorrect Entry',
                      description: 'label that indicates the incorrect entry',
                    })
              }
            />
          </TopCorrectDescriptionContainer>
          <BottomCorrectDescriptionContainer>
            <BodyText size={Size.SMALL} color={colors.textSecondary}>
              {correctValueDetails}
            </BodyText>
          </BottomCorrectDescriptionContainer>
        </CorrectDescriptionContainer>
      )}
      {(Boolean(minLabel) || Boolean(maxLabel)) && (
        <MinMaxLabelsContainer>
          {Boolean(minLabel) && (
            <BodyText
              text={minLabel}
              size={isMobileSized ? Size.CAPTION : Size.SMALL}
              color={colors.textSecondary}
              style={{ ...(isMobileSized && { fontWeight: 500 }) }}
              testID={minLabelTestId}
            />
          )}
          {Boolean(maxLabel) && (
            <BodyText
              text={maxLabel}
              size={isMobileSized ? Size.CAPTION : Size.SMALL}
              color={colors.textSecondary}
              style={{ ...(isMobileSized && { fontWeight: 500 }) }}
              testID={maxLabelTestId}
            />
          )}
        </MinMaxLabelsContainer>
      )}
    </BaseInput>
  )
}

export const RadioGroupRFF: FunctionComponent<FieldRenderProps<RadioGroupValue>> = ({
  input: { value, onChange, name, onFocus, onBlur },
  meta: { touched, error, active },
  buttons,
  label,
  labelInfoPopover,
  subLabel,
  subLabelComponent,
  labelSecondary,
  labelAlignment,
  largeSubLabel,
  readOnly,
  showDescription,
  correctValueDetails,
  correctValue,
  isYesNo = false,
  buttonType = RadioButtonType.CIRCLE_TEXT_OUTLINE,
  toolTipContent,
  toolTipTriggerConfig,
  accessibilityLabelledBy,
  baseInputStyle,
  buttonStyle,
  contextProps,
  disabled,
  minLabel,
  maxLabel,
  customTextStyle,
  labelContainerStyle,
  isRadioButtonsFlexRowDirection,
  textColor,
  indicatorColor,
}) => {
  return (
    <RadioGroup
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      label={label}
      labelInfoPopover={labelInfoPopover}
      subLabel={subLabel}
      subLabelComponent={subLabelComponent}
      labelSecondary={labelSecondary}
      labelAlignment={labelAlignment}
      largeSubLabel={largeSubLabel}
      selectedValue={value}
      buttons={buttons}
      name={name}
      error={touched && error}
      readOnly={readOnly}
      showDescription={showDescription}
      correctValueDetails={correctValueDetails}
      correctValue={correctValue}
      isYesNo={isYesNo}
      buttonType={buttonType}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      active={active}
      accessibilityLabelledBy={accessibilityLabelledBy}
      baseInputStyle={baseInputStyle}
      buttonStyle={buttonStyle}
      contextProps={contextProps}
      disabled={disabled}
      minLabel={minLabel}
      maxLabel={maxLabel}
      customTextStyle={customTextStyle}
      labelContainerStyle={labelContainerStyle}
      isRadioButtonsFlexRowDirection={isRadioButtonsFlexRowDirection}
      textColor={textColor}
      indicatorColor={indicatorColor}
    />
  )
}
